
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Proforma, ProformaStatus, Cliente, Producto, Tax, PaymentMethod } from '@shared/types';
import { useUser } from '@/contexts/UserContext';
import { formatearPorcentajeIrpf } from '@/utils/porcentaje';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { TrashIcon, PlusIcon, SaveIcon, BanIcon, Loader2Icon, EyeIcon, DownloadIcon } from '@/components/Icons';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DocumentPreview } from '@/components/ui/DocumentPreview';
import { generatePDF } from '@/utils/pdfGenerator';
import { Badge } from '@/components/ui/badge';
import { ArrowRightCircle, Loader2 } from 'lucide-react';

const createNewItem = (): import('@shared/types').InvoiceItem => ({ id: crypto.randomUUID(), concepto: '', cantidad: 1, precioUnitario: 0, taxId: '' });

const createNewProforma = (): Partial<Proforma> => ({
  numero: '',
  clienteId: '',
  fecha: new Date().toISOString().slice(0, 10),
  fechaVencimiento: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  items: [createNewItem()],
  estado: 'Pendiente',
  notas: '',
  aplicarIrpf: false,
  total: 0,
});

const statusOptions: { value: ProformaStatus; label: string }[] = [
  { value: 'Pendiente', label: 'Pendiente' },
  { value: 'Aceptada', label: 'Aceptada' },
  { value: 'Rechazada', label: 'Rechazada' },
  { value: 'Expirada', label: 'Expirada' },
];

export default function ProformaForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { fetchWithAuth, userProfile } = useUser();

  const [proforma, setProforma] = useState<Partial<Proforma>>(createNewProforma());
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [impuestos, setImpuestos] = useState<Tax[]>([]);
  const [metodosPago, setMetodosPago] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertPreview, setConvertPreview] = useState<any>(null);
  const [isConverting, setIsConverting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('showPreview') === 'true') {
      setShowPreview(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [clientesRes, productosRes, impuestosRes, metodosRes, nextNumRes] = await Promise.all([
          fetchWithAuth('/api/clientes'),
          fetchWithAuth('/api/productos'),
          fetchWithAuth('/api/impuestos'),
          fetchWithAuth('/api/metodos-pago'),
          fetchWithAuth('/api/proformas/next-number')
        ]);
        const clientesData = await clientesRes.json();
        const productosData = await productosRes.json();
        const impuestosData = await impuestosRes.json();
        const metodosData = await metodosRes.json();
        setClientes(clientesData);
        setProductos(productosData);
        setImpuestos(impuestosData);
        setMetodosPago(metodosData);

        if (id) {
          const proformaRes = await fetchWithAuth(`/api/proformas/${id}`);
          if (!proformaRes.ok) throw new Error('Proforma no encontrada');
          const proformaData = await proformaRes.json();
          setProforma(proformaData);
        } else {
          const { nextNumber } = await nextNumRes.json();
          setProforma({ ...createNewProforma(), numero: nextNumber });
        }
        setHasChanges(false);
      } catch (error) {
        toast.error('Error al cargar datos', { description: error instanceof Error ? error.message : 'Error desconocido' });
        navigate('/proformas');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, navigate, fetchWithAuth]);

  const handleFieldChange = useCallback((field: keyof Proforma, value: any) => {
    setProforma(prev => ({ ...prev, [field]: value }));
    if (!hasChanges) setHasChanges(true);
  }, [hasChanges]);

  const handleItemChange = (itemId: string, field: string, value: any) => {
    const newItems = proforma.items?.map(item =>
      item.id === itemId ? { ...item, [field]: value } : item
    );
    handleFieldChange('items', newItems);
  };

  const addNewItem = () => handleFieldChange('items', [...(proforma.items || []), createNewItem()]);
  const removeItem = (itemId: string) => handleFieldChange('items', proforma.items?.filter(item => item.id !== itemId));

  const productOptions = useMemo(() => productos.map(p => ({ label: p.nombre, value: p.nombre })), [productos]);

  const getTaxRate = useCallback((taxId: string) => {
    const tax = impuestos.find(t => t.id === taxId);
    return tax ? tax.rate : 0;
  }, [impuestos]);

  const { subtotal, iva, irpf, total } = useMemo(() => {
    const sub = proforma.items?.reduce((acc, item) => acc + item.cantidad * item.precioUnitario, 0) ?? 0;
    const ivaAmount = proforma.items?.reduce((acc, item) => acc + item.cantidad * item.precioUnitario * getTaxRate(item.taxId), 0) ?? 0;
    // Tasa del perfil del emisor (0 si no está definida), igual que el servidor
    const irpfRate = userProfile?.irpfRate ?? 0;
    const irpfAmount = proforma.aplicarIrpf ? sub * irpfRate : 0;
    return { subtotal: sub, iva: ivaAmount, irpf: irpfAmount, total: sub + ivaAmount - irpfAmount };
  }, [proforma.items, proforma.aplicarIrpf, userProfile?.irpfRate, getTaxRate]);

  // Constante para el aviso legal de IVA Exento
  const IVA_EXENTO_NOTICE = "Concepto exento de IVA - Art. 20. Uno. 26º (Ley 37/1992)";

  // Detectar si algún item tiene IVA Exento (iva = 0) y añadir aviso automáticamente
  useEffect(() => {
    if (!proforma.items) return;

    // Verificar si algún item tiene IVA exento (rate = 0)
    const hasExentoTax = proforma.items.some(item => getTaxRate(item.taxId) === 0);

    const currentNotas = proforma.notas || '';
    const hasExentoNotice = currentNotas.includes(IVA_EXENTO_NOTICE);

    // Si tiene IVA exento y no tiene el aviso, añadirlo
    if (hasExentoTax && !hasExentoNotice) {
      const newNotas = currentNotas
        ? `${currentNotas}\n\n${IVA_EXENTO_NOTICE}`
        : IVA_EXENTO_NOTICE;
      handleFieldChange('notas', newNotas);
    }
  }, [proforma.items, proforma.notas, handleFieldChange]);

  const handleSave = async () => {
    if (!proforma.clienteId) {
      toast.warning('Validación fallida', { description: 'Debes seleccionar un cliente.' });
      return;
    }

    setIsSaving(true);
    try {
      const dataToSend = { ...proforma, total };
      const response = await fetchWithAuth(id ? `/api/proformas/${id}` : '/api/proformas', {
        method: id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar');
      }
      toast.success(`Proforma ${id ? 'actualizada' : 'creada'} con éxito`);
      setHasChanges(false);
      navigate('/proformas');
    } catch (error) {
      toast.error('Error al guardar', { description: error instanceof Error ? error.message : 'Ocurrió un error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConvertClick = async () => {
    if (!id) return;

    try {
      const res = await fetchWithAuth(`/api/proformas/${id}/preview-conversion`);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message);
      }
      const preview = await res.json();
      setConvertPreview(preview);
      setShowConvertDialog(true);
    } catch (error) {
      toast.error('Error al preparar conversión', { description: error instanceof Error ? error.message : 'Error desconocido' });
    }
  };

  const handleConvertConfirm = async () => {
    if (!id) return;
    setIsConverting(true);

    try {
      const res = await fetchWithAuth(`/api/proformas/${id}/convertir`, { method: 'POST' });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message);
      }
      const result = await res.json();
      toast.success('Proforma convertida a factura', {
        description: `Factura ${result.factura.numero} creada exitosamente`,
        action: {
          label: 'Ver factura',
          onClick: () => navigate(`/facturas/${result.factura.id}/edit`)
        }
      });
      navigate('/proformas');
    } catch (error) {
      toast.error('Error al convertir', { description: error instanceof Error ? error.message : 'Error desconocido' });
    } finally {
      setIsConverting(false);
      setShowConvertDialog(false);
    }
  };

  const handleCancel = () => hasChanges ? setShowUnsavedDialog(true) : navigate('/proformas');

  const isConvertida = proforma.estado === 'Convertida';
  const canConvert = id && (proforma.estado === 'Pendiente' || proforma.estado === 'Aceptada');

  if (isLoading) return <div className="flex justify-center items-center h-64"><Loader2Icon className="animate-spin h-8 w-8 text-primary" /></div>;

  const selectedCliente = clientes.find(c => c.id === proforma.clienteId);
  const selectedPaymentMethod = metodosPago.find(m => m.id === proforma.paymentMethodId);

  return (
    <>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-8">
        <div className="flex justify-between items-center">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold tracking-tighter">{id ? `Editar Proforma #${proforma.numero}` : 'Nueva Proforma'}</h1>
            {isConvertida && (
              <div className="flex items-center gap-2">
                <Badge variant="success">Convertida</Badge>
                <span className="text-sm text-muted-foreground">Factura: {proforma.facturaGeneradaNumero}</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel} disabled={isSaving}><BanIcon className="mr-2" />Cancelar</Button>
            <Button variant="outline" onClick={() => setShowPreview(true)} disabled={!proforma.clienteId}><EyeIcon className="mr-2" />Vista Previa</Button>
            {canConvert && (
              <Button variant="secondary" onClick={handleConvertClick}>
                <ArrowRightCircle className="mr-2 h-4 w-4" />Convertir a Factura
              </Button>
            )}
            {!isConvertida && (
              <Button onClick={handleSave} disabled={isSaving || !hasChanges}><SaveIcon className="mr-2" />{isSaving ? 'Guardando...' : 'Guardar'}</Button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader><CardTitle>Información General</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <Select value={proforma.clienteId ?? ''} onValueChange={(val) => handleFieldChange('clienteId', val)} disabled={isConvertida}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                    <SelectContent>{clientes.map(c => <SelectItem key={c.id || ''} value={c.id || ''}>{c.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select value={proforma.estado ?? ''} onValueChange={(val) => handleFieldChange('estado', val as ProformaStatus)} disabled={isConvertida}>
                    <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
                    <SelectContent>
                      {statusOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                      {isConvertida && <SelectItem value="Convertida">Convertida</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fecha</Label>
                  <Input type="date" value={proforma.fecha ?? ''} onChange={e => handleFieldChange('fecha', e.target.value)} disabled={isConvertida} />
                </div>
                <div className="space-y-2">
                  <Label>Fecha de Vencimiento</Label>
                  <Input type="date" value={proforma.fechaVencimiento ?? ''} onChange={e => handleFieldChange('fechaVencimiento', e.target.value)} disabled={isConvertida} />
                </div>
                <div className="space-y-2">
                  <Label>Método de Pago</Label>
                  <Select value={proforma.paymentMethodId ?? ''} onValueChange={(val) => handleFieldChange('paymentMethodId', val)} disabled={isConvertida}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar método" /></SelectTrigger>
                    <SelectContent>
                      {metodosPago.map(m => <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Conceptos</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {proforma.items?.map((item) => (
                  <div key={item.id} className="p-4 bg-muted/30 rounded-lg border grid md:grid-cols-12 gap-4 relative">
                    <div className="md:col-span-5 space-y-2">
                      <Label>Concepto</Label>
                      <Combobox
                        options={productOptions}
                        value={item.concepto}
                        placeholder="Buscar producto..."
                        onChange={val => {
                          const product = productos.find(p => p.nombre === val);
                          handleItemChange(item.id, 'concepto', val);
                          if (product) {
                            handleItemChange(item.id, 'precioUnitario', product.precioUnitario ?? product.precio);
                          }
                        }}
                        disabled={isConvertida}
                      />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <Label>Cantidad</Label>
                      <Input type="number" value={item.cantidad} onChange={e => handleItemChange(item.id, 'cantidad', parseFloat(e.target.value) || 0)} disabled={isConvertida} />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <Label>Precio</Label>
                      <Input type="number" step="0.01" value={item.precioUnitario} onChange={e => handleItemChange(item.id, 'precioUnitario', parseFloat(e.target.value) || 0)} disabled={isConvertida} />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <Label>IVA</Label>
                      <Select value={item.taxId} onValueChange={val => handleItemChange(item.id, 'taxId', val)} disabled={isConvertida}>
                        <SelectTrigger><SelectValue placeholder="IVA" /></SelectTrigger>
                        <SelectContent>
                          {impuestos.map(t => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-1 flex items-end">
                      {!isConvertida && (
                        <Button variant="ghost" size="icon" onClick={() => removeItem(item.id)}><TrashIcon className="text-destructive" /></Button>
                      )}
                    </div>
                  </div>
                ))}
                {!isConvertida && (
                  <Button onClick={addNewItem} variant="outline"><PlusIcon className="mr-2" />Añadir línea</Button>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="lg:sticky lg:top-24 space-y-6">
            <Card>
              <CardHeader><CardTitle>Resumen</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{subtotal.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">IVA</span><span>{iva.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span></div>
                {proforma.aplicarIrpf && <div className="flex justify-between"><span className="text-muted-foreground">IRPF ({formatearPorcentajeIrpf(userProfile?.irpfRate)}%)</span><span className="text-destructive">-{irpf.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span></div>}
                <div className="text-xl font-bold flex justify-between pt-2 border-t"><span>Total</span><span>{total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span></div>
                <div className="flex items-center space-x-2 pt-4">
                  <Checkbox id="irpf" checked={proforma.aplicarIrpf} onCheckedChange={(checked) => handleFieldChange('aplicarIrpf', !!checked)} disabled={isConvertida} />
                  <Label htmlFor="irpf">Aplicar retención IRPF ({formatearPorcentajeIrpf(userProfile?.irpfRate)}%)</Label>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Notas</CardTitle></CardHeader>
              <CardContent>
                <Textarea value={proforma.notas ?? ''} onChange={e => handleFieldChange('notas', e.target.value)} placeholder="Notas para el cliente..." disabled={isConvertida} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cambios sin guardar</AlertDialogTitle>
            <AlertDialogDescription>Tienes cambios sin guardar. ¿Seguro que quieres salir?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, quedarme</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate('/proformas')}>Sí, descartar cambios</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>Vista Previa - Proforma {proforma.numero}</DialogTitle>
              <Button variant="outline" size="sm" onClick={() => generatePDF('document-preview-pdf', `proforma-${proforma.numero}.pdf`)}>
                <DownloadIcon className="mr-2 h-4 w-4" />Descargar PDF
              </Button>
            </div>
            <DialogDescription className="sr-only">
              Vista previa del documento de proforma antes de imprimir o descargar en PDF
            </DialogDescription>
          </DialogHeader>
          {selectedCliente && userProfile && proforma.items && (
            <DocumentPreview
              document={{
                ...proforma,
                tipo: 'PROFORMA',
                items: proforma.items || []
              } as any}
              client={selectedCliente}
              freelance={userProfile}
              taxes={impuestos}
              paymentMethod={selectedPaymentMethod}
            />
          )}
          <div className="flex justify-center pt-4 border-t">
            <Button variant="outline" onClick={() => generatePDF('document-preview-pdf', `proforma-${proforma.numero}.pdf`)}>
              <DownloadIcon className="mr-2 h-4 w-4" />Descargar PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert to Invoice Dialog */}
      <AlertDialog open={showConvertDialog} onOpenChange={(open) => { if (!isConverting) setShowConvertDialog(open); }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Convertir Proforma a Factura</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>Se creará una factura oficial a partir de esta proforma. Esta acción generará los datos VeriFactu necesarios para la AEAT.</p>
              {convertPreview && (
                <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Proforma:</span>
                    <span className="font-medium">{convertPreview.proforma.numero}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Nueva factura:</span>
                    <span className="font-medium text-primary">{convertPreview.facturaPreview.numero}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total:</span>
                    <span className="font-medium">{convertPreview.facturaPreview.total?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isConverting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvertConfirm} disabled={isConverting}>
              {isConverting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Convirtiendo...</> : 'Confirmar y Crear Factura'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
